<template>
  <input v-model="value"
         @change="toggle"
         :id="'select-' + list + '-' + index"
         :class="checkboxClass"
         type="checkbox"
         :name="name"
         :value="id" />
  <label :for="'select-' + list + '-' + index" class="mr-0"
         @mouseenter="setLastMouseEnterIdValue"
         @mouseleave="resetLastMouseEnterIdValue"
         v-tooltip title="Check an item, hold Shift, then click another to select the range. Uncheck two items to deselect." data-delay="1000"></label>
</template>

<script>
import {mapActions, mapState} from 'pinia'
import {useCheckboxListStore} from "../stores/checkboxList"

export default {
  emits: ['update:modelValue'],

  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    list: {
      type: String,
      default: 'default'
    },
    index: {
      type: Number,
      required: true
    },
    name: {
      type: String,
      default: 'ids[]'
    },
    id: {
      type: [Number, String],
      default: 0
    },
  },

  data() {
    return {
      value: this.modelValue,
      isShiftPressed: false,
    }
  },

  computed: {
    ...mapState(useCheckboxListStore, [
      'isChecked',
      'checkedIds',
      'listSize',
      'checkedCount',
      'rangeSelectionMode',
      'lastChangedIndex',
      'lastMouseEnterIndex',
    ]),

    checkboxClass() {
      return {
        'custom-checkbox': (this.lastChangedIndexValue === this.index) || !(this.isShiftSelected || this.isShiftUnselected),
        'custom-checkbox-range-selected': this.isShiftSelected && (this.lastChangedIndexValue !== this.index),
        'custom-checkbox-range-unselected': this.isShiftUnselected && (this.lastChangedIndexValue !== this.index),
      };
    },

    checkedCountValue() {
      return this.checkedCount(this.list);
    },

    isShiftSelected() {
      return this.getShiftSelected('check');
    },

    isShiftUnselected() {
      return this.getShiftSelected('uncheck');
    },

    isCheckedValue() {
      return this.isChecked(this.id, this.list);
    },

    rangeSelectionModeValue() {
      return this.rangeSelectionMode(this.list);
    },

    lastChangedIndexValue() {
      return this.lastChangedIndex(this.list);
    },

    lastMouseEnterIndexValue() {
      return this.lastMouseEnterIndex(this.list);
    },

  },

  methods: {
    ...mapActions(useCheckboxListStore, ['check', 'uncheck', 'setAll', 'setLastMouseEnterId', 'setRangeSelectionMode', 'setLastChangedId']),

    toggle() {
      if (!this.isShiftPressed || (this.isShiftPressed && (this.lastChangedIndexValue === this.index))) {
        // Handle checkbox value update.
        if (this.value) {
          this.check(this.id, this.list);
          this.setRangeSelectionMode('check', this.list);
        }
        if (!this.value) {
          this.uncheck(this.id, this.list);
          this.setRangeSelectionMode('uncheck', this.list);
        }
        this.setLastChangedId(this.id, this.list);
        return;
      }

      if (this.rangeSelectionModeValue === 'check') {
        this.value = true;
        this.check(this.id, this.list);
      }
      if (this.rangeSelectionModeValue === 'uncheck') {
        this.value = false;
        this.uncheck(this.id, this.list);
      }
      this.setLastChangedId(this.id, this.list);
    },

    getShiftSelected(mode) {
      if (mode === 'none') return false;
      if (!this.isShiftPressed) return false;
      if (this.lastMouseEnterIndexValue < 0) return false;
      if (this.lastChangedIndexValue < 0) return false;
      if (this.rangeSelectionModeValue !== mode) return false;

      const min = Math.min(this.lastMouseEnterIndexValue, this.lastChangedIndexValue);
      const max = Math.max(this.lastMouseEnterIndexValue, this.lastChangedIndexValue);

      return (this.index >= min) && (this.index <= max);
    },

    handleKeyDown(event) {
      if (event.key === 'Shift') this.isShiftPressed = true;
    },

    handleKeyUp(event) {
      if (event.key === 'Shift') this.isShiftPressed = false;
    },

    setLastMouseEnterIdValue() {
      this.setLastMouseEnterId(this.id, this.list);
    },

    resetLastMouseEnterIdValue() {
      this.setLastMouseEnterId(null, this.list);
    }
  },

  watch: {
    checkedCountValue: {
      handler(value) {
        // Watch checked item count to toggle "Select all" checkbox if the current checkbox changed its state.
        const allChecked = (value === this.listSize(this.list));
        this.setAll(allChecked, this.list);
      },
    },
    modelValue: {
      handler(value) {
        // Watch the "modelValue" property for changes.
        this.value = value;
      },
    },
    value: {
      handler(value) {
        // Watch the value changed from any source
        // (e.g., when checkbox clicked or when check/uncheck all is called)
        // to emit the "update:modelValue" event.
        this.$emit('update:modelValue', value);
      }
    },
    isCheckedValue: {
      handler(value) {
        // Watch the checkbox value changed from outside the component. E.g., when check/uncheck all is called.
        if (value !== this.value) {
          this.value = value;
        }
      },
    },
    lastChangedIndexValue: {
      handler(value, oldValue) {
        if (value < 0) return;
        if (oldValue < 0) return;
        if (this.index === value) return;
        if (!this.isShiftPressed) return;
        if (this.rangeSelectionModeValue === 'none') return;

        if ((this.index >= Math.min(value, oldValue)) && (this.index <= Math.max(value, oldValue))) {
          if (this.rangeSelectionModeValue === 'check') {
            this.check(this.id, this.list);
            return;
          }
          this.uncheck(this.id, this.list);
        }
      },
    },
  },

  mounted() {
    if (this.modelValue) {
      this.check(this.id, this.list);
    }

    if (!this.modelValue) {
      this.uncheck(this.id, this.list);
    }
  },

  created(){
    document.addEventListener("keydown", this.handleKeyDown);
    document.addEventListener("keyup", this.handleKeyUp);
  }
}
</script>
